import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput, sanitizeRoomCode } from '../../utils/sanitize';
import { getRandomHeadlineImage, getHeadlineImagePath } from '../../utils/headlineImages';
import SEO from '../../utils/SEO';
import GoogleAdSquare from '../googleads/GoogleAdSquare';

export default function JoinRoom() {
  const [roomCode, setRoomCode] = useState(['', '', '', '']);
  const [playerName, setPlayerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [randomImage, setRandomImage] = useState<string>("");
  const navigate = useNavigate();
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  useEffect(() => {
    setRandomImage(getRandomHeadlineImage());
  }, []);

  const handleJoinRoom = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const sanitizedName = sanitizeInput(playerName);
    if (!sanitizedName) {
      setError('Please enter your name');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const roomId = roomCode.join('');
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          playerName: sanitizedName,
          iconId: 0 // Initialize with default icon
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to join room');
      }

      const data = await response.json(); 
      navigate(`/app/room/${roomId}`, {
        state: { 
          isAdmin: false, 
          playerName: data.playerName, // Use unique name from backend
          playerId: data.playerId,
          iconId: 0 
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const sanitizedValue = sanitizeRoomCode(value);
    if (!sanitizedValue && value !== '') return; // Don't update if invalid input
    
    const newRoomCode = [...roomCode];
    newRoomCode[index] = sanitizedValue;
    setRoomCode(newRoomCode);

    // Move to next input if value is entered
    if (sanitizedValue && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !roomCode[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = sanitizeRoomCode(e.clipboardData.getData('text'));
    if (pastedData) {
      const newRoomCode = [...roomCode];
      for (let i = 0; i < pastedData.length; i++) {
        newRoomCode[i] = pastedData[i];
      }
      setRoomCode(newRoomCode);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(sanitizeInput(e.target.value));
  };

  const handleBack = async () => {
    try {
      // Only attempt to delete if a room code is partially or fully entered
      const roomId = roomCode.join('');
      if (roomId.length > 0) {
        const response = await fetch(`/api/rooms/${roomId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error('Failed to delete room');
        }
      }
    } catch (error) {
      console.error('Error deleting room:', error);
    } finally {
      // Navigate to static landing page
      window.location.replace('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <SEO 
        title="Join Game Room"
        description="Join an existing Spyfall game room. Enter your name and the room code to start playing with friends in this exciting social deduction game."
        keywords="join spyfall game, room code, multiplayer game, social deduction game"
        noIndex={true}
      />
      {/* Google AdSense Ad */}
      {/*<GoogleAdSquare/>*/}
      <div className="max-w-md mx-auto px-4 py-16">
        {/* Image Card */}
        <div className="relative w-64 h-64 bg-gradient-to-br from-red-900 to-red-700 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center border-2 border-red-500 overflow-hidden mb-8 mx-auto">
          <img
            src={getHeadlineImagePath(randomImage)}
            alt="Join Room"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-[3px] bg-gray-900/20 rounded-md flex flex-col items-center justify-center p-4">
            <div className="text-red-500 font-bold text-sm mb-2"></div>
            <div className="text-xl font-bold"></div>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Join Existing Room</h1>
          <p className="text-gray-400">Enter the room code to join a game</p>
        </div>

        <form onSubmit={handleJoinRoom} className="space-y-6">
          <div>
            <label htmlFor="playerName" className="block text-sm font-medium text-gray-300 mb-2">
              Your Name
            </label>
            <Input
              id="playerName"
              type="text"
              value={playerName}
              onChange={handleNameChange}
              className="w-full bg-gray-800 border-gray-700 text-white"
              placeholder="Enter your name"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="roomCode" className="block text-sm font-medium text-gray-300 mb-2">
              Room Code
            </label>
            <div className="flex gap-2 justify-center">
              {roomCode.map((char, index) => (
                <Input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="text"
                  maxLength={1}
                  value={char}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-12 h-12 text-center text-xl bg-gray-800 border-gray-700 text-white uppercase ${
                    char ? 'text-red-500' : ''
                  }`}
                  placeholder="A"
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-red-500 hover:bg-red-600"
              disabled={roomCode.some(char => !char) || !playerName.trim() || isLoading}
            >
              {isLoading ? 'Joining...' : 'Join Room'}
            </Button>
            <Button
              type="button"
              className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              variant="outline"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          </div>
        </form>

          {/* Google AdSense Ad (Bottom) */}
          <div className="mt-6">
            <GoogleAdSquare />
          </div>
      </div>
    </div>
  );
} 